import { EMAIL_PLACEHOLDER, FIRST_NAME_PLACEHOLDER, LAST_NAME_PLACEHOLDER, PHONE_PLACEHOLDER } from '@common/schemas';
import {
  createEditOfaUtilisateurFormSchemaWithAsyncEmail,
  EditOfaUtilisateurFormResult,
  editOfaUtilisateurFormSchema,
} from '@common/schemas/src/forms/organisme/utilisateur/edit-ofa-utilisateur-form.schema';
import {
  CreateOrganismeUtilisateurSimpleArgs,
  UpdateOrganismeUtilisateurSimpleArgs,
  useAddOrganismeUtilisateurSimpleMutation,
  useLazyIsEmailAvailableQuery,
  useUpdateOrganismeUtilisateurSimpleMutation,
  UtilisateurDto,
  UtilisateurRole,
} from '@common/services';
import { DsfrModal, Form, FormMethodsRef, TextField } from '@common/ui';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ZodObject } from 'zod';

export const EditOfaUtilisateurModal = ({
                                          dsfrModal,
                                          organismeId,
                                          isAddMode,
                                          utilisateur,
                                        }: EditAdminInfoModalProps) => {
  const buttonSubmitRef = useRef<HTMLButtonElement | null>(null!);
  const [addOrganismeUtilisateur] = useAddOrganismeUtilisateurSimpleMutation();
  const [updateOrganismeUtilisateur] = useUpdateOrganismeUtilisateurSimpleMutation();
  const [isEmailAvailableQuery] = useLazyIsEmailAvailableQuery();

  let timeoutEmailId: number | null = null;

  const emailAsyncValidation = (email: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (timeoutEmailId !== null) {
        window.clearTimeout(timeoutEmailId);
      }

      timeoutEmailId = window.setTimeout(async () => {
        try {
          const result = await isEmailAvailableQuery({ email }).unwrap();
          resolve(result);
        } catch (error) {
          resolve(false);
        }
      }, 500);
    });
  };

  // suppression du timeout avec use-effect lors de la destruction du composant
  useEffect(() => {
    return () => {
      if (timeoutEmailId !== null) {
        clearTimeout(timeoutEmailId);
      }
    };
  }, []);

  const [formSchema, setFormSchema] = useState<ZodObject>(createEditOfaUtilisateurFormSchemaWithAsyncEmail(emailAsyncValidation));
  const formMethodsRef = useRef<FormMethodsRef | null>(null);


  async function handleSubmit(formResult: EditOfaUtilisateurFormResult) {
    const baseDto = {
      nom: formResult.nom,
      prenom: formResult.prenom,
      email: formResult.email,
      telephone: formResult.telephone,
      travail: '', // on met un travail vide pour l'utilisateur simple
    };
    if (isAddMode) {
      const createArgs: CreateOrganismeUtilisateurSimpleArgs = {
        id: organismeId,
        utilisateurDto: { ...baseDto, role: UtilisateurRole.SIMPLE },
      };
      await addOrganismeUtilisateur(createArgs).unwrap();
      // clear du formulaire
      formMethodsRef.current?.reset();
    } else if (utilisateur) {
      const updateArgs: UpdateOrganismeUtilisateurSimpleArgs = {
        id: organismeId,
        utilisateurId: utilisateur.id,
        utilisateurDto: { ...baseDto, id: utilisateur.id },
      };
      updateOrganismeUtilisateur(updateArgs);
    }

    dsfrModal.close();
  }

  const formValues: EditOfaUtilisateurFormResult = useMemo(() => {
    return {
      nom: utilisateur?.nom ?? '',
      prenom: utilisateur?.prenom ?? '',
      email: utilisateur?.email ?? '',
      telephone: utilisateur?.telephone ?? '',
    };
  }, [utilisateur]);

  useEffect(() => {
    if (!isAddMode) {
      setFormSchema(editOfaUtilisateurFormSchema.safeExtend({
        email: editOfaUtilisateurFormSchema.shape.email.readonly(),
      }));
    } else {
      setFormSchema(createEditOfaUtilisateurFormSchemaWithAsyncEmail(emailAsyncValidation).safeExtend({
        email: createEditOfaUtilisateurFormSchemaWithAsyncEmail(emailAsyncValidation).shape.email,
      }));
    }
  }, [isAddMode]);


  return (
    <dsfrModal.Component
      title={''}
      size="large"
      topAnchor={false}
      buttons={[
        {
          children: 'ANNULER',
          className: 'text-red-400!',
          doClosesModal: true,
          priority: 'tertiary',
        },
        {
          children: 'VALIDER',
          doClosesModal: false,
          priority: 'primary',
          onClick: () => buttonSubmitRef.current?.click(),
        },
      ]}
    >
      <div className={'flex flex-col p-6'}>

        <div className={'flex flex-col gap-6'}>

          <h4 className={'m-0!'}>{isAddMode ? 'Ajout utilisateur' : 'Modification Utilisateur'}</h4>

          <span className={'text-sm text-[#484D7A]'}>Les champs marqués d’un * sont obligatoires</span>

          <Form<EditOfaUtilisateurFormResult> schema={formSchema}
                                              values={formValues}
                                              className={'flex flex-col gap-6'}
                                              onSubmit={handleSubmit}
                                              buttonSubmitRef={buttonSubmitRef}
                                              methodsRef={formMethodsRef}
          >

            <TextField name="nom" title="Nom" placeholder={LAST_NAME_PLACEHOLDER} />
            <TextField name="prenom" title="Prénom" placeholder={FIRST_NAME_PLACEHOLDER} />
            <TextField name="email" title="Courriel" placeholder={EMAIL_PLACEHOLDER} />
            <TextField name="telephone" title="Téléphone" placeholder={PHONE_PLACEHOLDER} />

          </Form>
        </div>
      </div>
    </dsfrModal.Component>
  );
};

interface EditAdminInfoModalProps {
  dsfrModal: DsfrModal;
  organismeId: number;
  isAddMode: boolean;
  utilisateur?: UtilisateurDto;
}

